import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Upload, FileText, Store, Phone, MapPin, Sparkles, Layers, Cog, Cpu, Zap } from "lucide-react";
import { formatEUR } from "@/lib/format";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1769147339214-076740872485?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHx0ZWNobmljYWwlMjBwcm90b3R5cGUlMjBtZWNoYW5pY2FsJTIwcGFydHxlbnwwfHx8fDE3ODQwMzA0OTN8MA&ixlib=rb-4.1.0&q=85";
const PRINTER_IMG = "https://images.unsplash.com/photo-1642969164999-979483e21601?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHwzZCUyMHByaW50aW5nJTIwbWFjaGluZSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzg0MDMwNDkzfDA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  { icon: Cpu, title: "Modelação 3D", href: "/servicos#modelacao", desc: "Desenvolvimento paramétrico, otimização, engenharia inversa." },
  { icon: Layers, title: "Impressão 3D", href: "/servicos#impressao", desc: "Peças funcionais, técnicas, personalizadas, séries." },
  { icon: Cog, title: "Prototipagem", href: "/servicos#prototipagem", desc: "Validação de conceito, encaixe e teste funcional." },
  { icon: Sparkles, title: "Desenvolvimento de produto", href: "/servicos#desenvolvimento", desc: "Da ideia à produção, com acompanhamento total." },
  { icon: Zap, title: "Pequenas séries", href: "/servicos#series", desc: "Produção por lotes sem custo de moldes." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="anim-in">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 cad-grid opacity-70 pointer-events-none" />
        <div className="container-x grid lg:grid-cols-12 gap-10 py-20 lg:py-28 relative">
          <div className="lg:col-span-7">
            <p className="tech-label mb-6">
              <span className="text-brand">■</span> cadditive · Barcelos, PT · fabrico aditivo
            </p>
            <h1 className="font-bold tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              Da <span className="italic font-normal">ideia</span> ao <span className="text-brand">produto físico</span>.
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Modelação 3D, prototipagem e fabrico aditivo para particulares e empresas.
              Desenvolvemos, produzimos e entregamos peças técnicas, protótipos e produtos personalizados em Portugal.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/pedido-orcamento" className="btn-primary" data-testid="hero-cta-quote">
                Pedir orçamento <ArrowRight size={16} />
              </Link>
              <Link to="/pedido-orcamento" className="btn-outline" data-testid="hero-cta-upload">
                <Upload size={16} /> Enviar ficheiro
              </Link>
              <Link to="/loja" className="btn-outline" data-testid="hero-cta-shop">
                <Store size={16} /> Ver produtos
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="text-3xl font-bold">100%</div>
                <div className="tech-label mt-1">produção local</div>
              </div>
              <div>
                <div className="text-3xl font-bold">48h</div>
                <div className="tech-label mt-1">resposta média</div>
              </div>
              <div>
                <div className="text-3xl font-bold">2+</div>
                <div className="tech-label mt-1">bambu lab farm</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="crosshair-corners relative aspect-[4/5] overflow-hidden border border-border bg-secondary">
              <img src={HERO_IMG} alt="peças técnicas em desenvolvimento" className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest bg-background/80 backdrop-blur px-2 py-1 border border-border">
                spec · V.2026.01
              </div>
              <div className="absolute bottom-3 right-3 mono text-[10px] uppercase tracking-widest bg-background/80 backdrop-blur px-2 py-1 border border-border">
                cad-ref · #0247
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section className="border-t border-border bg-card">
        <div className="container-x py-20">
          <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
            <div>
              <p className="tech-label mb-3">01 · Serviços</p>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight max-w-lg">Um estúdio, todos os passos.</h2>
            </div>
            <Link to="/servicos" className="btn-outline text-sm" data-testid="home-services-cta">
              Ver serviços <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <Link
                to={s.href}
                key={s.title}
                data-testid={`home-service-${i}`}
                className={`group relative border border-border bg-background p-7 hover:border-foreground transition-colors ${
                  i === 0 ? "lg:col-span-2 lg:row-span-2 lg:min-h-[280px]" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <s.icon size={28} className="text-brand" />
                  <span className="mono text-[10px] text-muted-foreground uppercase">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className={`mt-6 font-semibold tracking-tight ${i === 0 ? "text-3xl" : "text-xl"}`}>{s.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                <span className="mt-8 inline-flex items-center gap-1 text-sm text-brand opacity-0 group-hover:opacity-100 transition-opacity">
                  Saber mais <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FERRAMENTAS · MATERIAIS + STL ANALYZER */}
      <section className="border-t border-border">
        <div className="container-x py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="tech-label mb-3">02 · Ferramentas</p>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">Sem esperar por email.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/ferramentas/analise-stl" data-testid="home-stl-tool"
              className="group border border-border bg-background p-8 hover:border-foreground transition-colors relative overflow-hidden">
              <div className="absolute -right-8 -top-8 opacity-10 group-hover:opacity-30 transition-opacity">
                <Cpu size={180} />
              </div>
              <p className="mono text-[10px] uppercase text-muted-foreground">Ferramenta interativa · 3D</p>
              <h3 className="text-3xl font-bold tracking-tight mt-4">Analisador STL <span className="text-brand">instantâneo</span></h3>
              <p className="mt-4 text-sm text-muted-foreground max-w-md leading-relaxed">
                Carrega o teu ficheiro STL e obtém visualização 3D, dimensões, peso estimado e preço em segundos. Processado localmente no browser.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-brand text-sm font-medium">
                Experimentar <ArrowRight size={14} />
              </span>
            </Link>
            <Link to="/materiais" data-testid="home-materials-tool"
              className="group border border-border bg-background p-8 hover:border-foreground transition-colors relative overflow-hidden">
              <div className="absolute -right-8 -top-8 opacity-10 group-hover:opacity-30 transition-opacity">
                <Layers size={180} />
              </div>
              <p className="mono text-[10px] uppercase text-muted-foreground">Guia técnico</p>
              <h3 className="text-3xl font-bold tracking-tight mt-4">Qual o material <span className="text-brand">certo</span>?</h3>
              <p className="mt-4 text-sm text-muted-foreground max-w-md leading-relaxed">
                PLA, PETG, PETG-CF, ASA, TPU… Explora fichas técnicas visuais ou responde a 3 perguntas para receber uma recomendação.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-brand text-sm font-medium">
                Ver materiais <ArrowRight size={14} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESSO */}
      <section className="border-t border-border">
        <div className="container-x py-20 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="tech-label mb-3">03 · Processo</p>
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">
              Um método técnico. <br />
              <span className="text-muted-foreground">Sem complicações.</span>
            </h2>
            <ol className="mt-10 space-y-6">
              {[
                ["Pedido & análise", "Envia ideia, ficheiro ou fotografia. Analisamos requisitos técnicos."],
                ["Proposta", "Recebes um orçamento claro, com prazos e materiais recomendados."],
                ["Modelação & prototipagem", "Desenvolvemos ou preparamos o modelo. Iterações rápidas."],
                ["Produção", "Impressão, acabamento, montagem e controlo de qualidade."],
                ["Expedição", "Envio em Portugal continental ou recolha em Barcelos."],
              ].map(([t, d], i) => (
                <li key={t} className="flex gap-5 border-l border-border pl-5">
                  <span className="mono text-brand text-sm shrink-0">0{i + 1}</span>
                  <div>
                    <h4 className="font-semibold">{t}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="crosshair-corners relative aspect-[4/5] border border-border overflow-hidden">
            <img src={PRINTER_IMG} alt="impressão 3D em processo" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* PRODUTOS DESTACADOS */}
      {featured.length > 0 && (
        <section className="border-t border-border bg-card">
          <div className="container-x py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <p className="tech-label mb-3">03 · Loja</p>
                <h2 className="text-4xl font-bold tracking-tight">Produtos cadditive.</h2>
              </div>
              <Link to="/loja" className="btn-outline text-sm">Ver todos <ArrowRight size={14} /></Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((p) => (
                <Link key={p.product_id} to={`/loja/${p.product_id}`} className="group border border-border bg-background p-4 hover:border-foreground transition-colors" data-testid={`home-product-${p.sku}`}>
                  <div className="aspect-square bg-secondary overflow-hidden mb-4">
                    {p.images?.[0] ? (
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground mono text-xs uppercase">sem imagem</div>
                    )}
                  </div>
                  <p className="mono text-[10px] uppercase text-muted-foreground">{p.sku}</p>
                  <h3 className="font-semibold mt-1">{p.name}</h3>
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-bold">{formatEUR(p.price)}</span>
                    <span className="mono text-[10px] uppercase text-muted-foreground">{p.material}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PORTFOLIO PREVIEW */}
      {portfolio.length > 0 && (
        <section className="border-t border-border">
          <div className="container-x py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <p className="tech-label mb-3">04 · Portefólio</p>
                <h2 className="text-4xl font-bold tracking-tight">Trabalhos recentes.</h2>
              </div>
              <Link to="/portfolio" className="btn-outline text-sm">Ver portefólio <ArrowRight size={14} /></Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {portfolio.map((p) => (
                <Link to={`/portfolio/${p.slug}`} key={p.slug} className="group border border-border overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden bg-secondary">
                    {p.images?.[0] && <img src={p.images[0]} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt={p.title} />}
                  </div>
                  <div className="p-5">
                    <p className="tech-label">{p.category}</p>
                    <h4 className="font-semibold mt-2">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA FINAL */}
      <section className="border-t border-border bg-foreground text-background">
        <div className="container-x py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="tech-label mb-4 text-background/60">Vamos começar</p>
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">
              Tem um projeto? <span className="text-brand">Falamos.</span>
            </h2>
            <p className="mt-4 text-background/70 max-w-md">
              Envie o seu ficheiro, uma fotografia ou apenas a ideia. Respondemos em 48h.
            </p>
          </div>
          <div className="flex flex-col gap-4 md:items-end">
            <Link to="/pedido-orcamento" className="btn-primary" data-testid="home-final-cta">
              Pedir orçamento <ArrowRight size={16} />
            </Link>
            <div className="flex flex-col md:items-end gap-1 mono text-xs text-background/70 uppercase tracking-wider">
              <span className="flex items-center gap-2"><MapPin size={12} /> Barcelos, Portugal</span>
              <span className="flex items-center gap-2"><Phone size={12} /> +351 912 000 000</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
